import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Search, CheckCircle, ArrowRight, DollarSign, Clock, Award } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import { processSteps } from '../mock';
import ContactPopup from '../components/ContactPopup';

const TrademarkSearch = () => {
  const [isPopupOpen, setIsPopupOpen] = useState(false);

  const benefits = [
    'Identify potential conflicts before filing',
    'Save time and money on rejected applications',
    'Assess the strength of your mark',
    'Discover existing trademarks in your industry',
    'Avoid costly legal disputes and rebranding',
    'Make informed decisions about your brand',
    'Understand competitive landscape',
    'Receive detailed analysis and recommendations'
  ];

  return (
    <div className="min-h-screen bg-slate-950">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-b from-slate-900 to-slate-950 py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-4xl mx-auto">
            <div className="inline-flex items-center bg-amber-500/10 border border-white/20 rounded-full px-4 py-2 mb-6">
              <Search className="h-4 w-4 text-amber-500 mr-2" />
              <span className="text-sm text-amber-500 font-medium">Comprehensive Analysis</span>
            </div>
            <h1 className="text-4xl sm:text-5xl font-bold text-white mb-6">
              Trademark <span className="text-transparent bg-clip-text bg-gradient-to-r from-amber-400 to-amber-600">Search</span>
            </h1>
            <p className="text-xl text-gray-300 leading-relaxed mb-8">
              Before filing your trademark, discover potential conflicts with our comprehensive trademark search service. We analyze federal databases, state records, and common law uses to assess your mark's availability.
            </p>
            <Button 
              size="lg" 
              onClick={() => setIsPopupOpen(true)}
              className="bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 text-white font-semibold shadow-xl transform transition-transform hover:scale-105"
            >
              Request a Search
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </div>
        </div>
      </section>

      {/* Key Benefits */}
      <section className="py-20 bg-slate-950">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
              Why Conduct a Trademark Search?
            </h2>
            <p className="text-xl text-gray-400 max-w-2xl mx-auto">
              A thorough search is essential before investing in your brand identity.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {benefits.map((benefit, index) => (
              <div key={index} className="flex items-start space-x-3 bg-slate-900/40 border border-white/20 rounded-lg p-4 hover:border-white/40 hover:shadow-lg hover:shadow-amber-500/20 hover:scale-[1.02] transition-colors">
                <CheckCircle className="h-5 w-5 text-amber-500 flex-shrink-0 mt-0.5" />
                <span className="text-gray-300">{benefit}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Search Coverage */}
      <section className="py-20 bg-slate-900/40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
              What We Search
            </h2>
            <p className="text-xl text-gray-400 max-w-2xl mx-auto">
              Our comprehensive search covers multiple sources and databases.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              { title: 'Federal Databases', desc: 'Complete USPTO trademark database search including active, pending, and abandoned marks.', features: ['USPTO TESS database', 'Live trademarks', 'Pending applications', 'Historical records'] },
              { title: 'State & Common Law', desc: 'State trademark registrations and common law uses not registered federally.', features: ['State registrations', 'Business name databases', 'Domain names', 'Common law marks'] },
              { title: 'Online Presence', desc: 'Social media handles, domain names, and web presence analysis.', features: ['Domain availability', 'Social media handles', 'Business directories', 'Web presence'] }
            ].map((category, index) => (
              <Card key={index} className="bg-slate-900/60 border-white/20 hover:border-white/40 hover:shadow-lg hover:shadow-amber-500/20 hover:scale-[1.02] transition-colors">
                <CardHeader>
                  <CardTitle className="text-white text-lg">{category.title}</CardTitle>
                  <CardDescription className="text-gray-400">{category.desc}</CardDescription>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-2">
                    {category.features.map((feature, idx) => (
                      <li key={idx} className="flex items-center text-gray-300 text-sm">
                        <CheckCircle className="h-4 w-4 text-amber-500 mr-2 flex-shrink-0" />
                        {feature}
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Process - 3 Steps */}
      <section className="py-20 bg-slate-900/40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
              Our Streamlined Process
            </h2>
            <p className="text-xl text-gray-400 max-w-2xl mx-auto">
              A systematic approach to uncover potential trademark conflicts.
            </p>
          </div>

          {/* Desktop Grid */}
          <div className="hidden md:grid grid-cols-1 md:grid-cols-3 gap-8">
            {processSteps.map((step, index) => (
              <div key={step.step} className="relative">
                <div className="bg-slate-900 border border-white/20 rounded-xl p-8 h-full hover:border-white/40 hover:shadow-xl hover:shadow-white/10 hover:scale-[1.02] transition-all group">
                  <div className="bg-gradient-to-br from-amber-500 to-amber-600 text-white w-16 h-16 rounded-full flex items-center justify-center font-black text-2xl mb-6 shadow-xl group-hover:scale-110 transition-transform">
                    {step.step}
                  </div>
                  <h3 className="text-white font-bold text-xl mb-3">{step.title}</h3>
                  <p className="text-gray-400 leading-relaxed">{step.description}</p>
                </div>
              </div>
            ))}
          </div>

          {/* Mobile Slider */}
          <div className="md:hidden overflow-x-auto scrollbar-hide">
            <div className="flex gap-4 pb-4" style={{ scrollSnapType: 'x mandatory' }}>
              {processSteps.map((step) => (
                <div key={step.step} className="flex-shrink-0 w-[300px]" style={{ scrollSnapAlign: 'center' }}>
                  <div className="bg-slate-900 border border-white/20 rounded-xl p-6 h-full">
                    <div className="bg-gradient-to-br from-amber-500 to-amber-600 text-white w-14 h-14 rounded-full flex items-center justify-center font-black text-xl mb-4 shadow-xl">
                      {step.step}
                    </div>
                    <h3 className="text-white font-bold text-lg mb-3">{step.title}</h3>
                    <p className="text-gray-400 text-sm leading-relaxed">{step.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* What's Included */}
      <section className="py-20 bg-slate-900/40">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
              What's Included
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <Card className="bg-slate-900/60 border-white/20 text-center">
              <CardHeader>
                <div className="bg-gradient-to-br from-amber-500/10 to-amber-600/10 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                  <DollarSign className="h-8 w-8 text-amber-500" />
                </div>
                <CardTitle className="text-white">Detailed Report</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-400 text-sm">
                  Comprehensive analysis with all findings documented and explained.
                </p>
              </CardContent>
            </Card>

            <Card className="bg-slate-900/60 border-white/20 text-center">
              <CardHeader>
                <div className="bg-gradient-to-br from-amber-500/10 to-amber-600/10 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Clock className="h-8 w-8 text-amber-500" />
                </div>
                <CardTitle className="text-white">Quick Turnaround</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-400 text-sm">
                  Most searches completed and delivered within 3-5 business days.
                </p>
              </CardContent>
            </Card>

            <Card className="bg-slate-900/60 border-white/20 text-center">
              <CardHeader>
                <div className="bg-gradient-to-br from-amber-500/10 to-amber-600/10 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Award className="h-8 w-8 text-amber-500" />
                </div>
                <CardTitle className="text-white">Expert Analysis</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-400 text-sm">
                  Professional recommendations based on search results and risk assessment.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* FAQ Section - Matching Homepage Style */}
      <section className="py-20 relative overflow-hidden bg-gradient-to-b from-slate-950 via-slate-900 to-slate-950">
        {/* Decorative elements */}
        <div className="absolute top-10 right-10 w-64 h-64 bg-amber-500/5 rounded-full blur-3xl" />
        <div className="absolute bottom-10 left-10 w-96 h-96 bg-blue-500/5 rounded-full blur-3xl" />
        
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center mb-16">
            <div className="inline-block mb-4">
              <span className="text-amber-500 font-semibold uppercase tracking-wider text-sm">Got Questions?</span>
            </div>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black text-white mb-4">
              Frequently Asked
              <span className="block text-transparent bg-clip-text bg-gradient-to-r from-amber-400 to-amber-600">Questions</span>
            </h2>
            <p className="text-xl text-gray-400 max-w-2xl mx-auto">
              Everything you need to know about trademark searches.
            </p>
          </div>

          <div className="grid lg:grid-cols-2 gap-6">
            {[
              { q: "Why do I need a trademark search?", a: "A trademark search helps identify potential conflicts with existing marks before you invest in filing fees, branding, and marketing. It significantly reduces the risk of application rejection and costly legal disputes." },
              { q: "What databases do you search?", a: "We search the USPTO federal trademark database, state trademark registries, common law sources including business directories, domain names, social media handles, and industry-specific databases to provide comprehensive coverage." },
              { q: "How long does a trademark search take?", a: "Most trademark searches are completed within 3-5 business days. Rush services are available for urgent cases. You'll receive a detailed report with our analysis and recommendations." },
              { q: "What if the search finds conflicts?", a: "If we find potential conflicts, we provide a risk assessment and recommendations. Options may include modifying your mark, choosing a different name, or proceeding with a calculated risk based on the specific circumstances." },
              { q: "Is a trademark search required?", a: "While not legally required, a trademark search is highly recommended and considered best practice. The USPTO will refuse applications that conflict with existing marks, and the filing fees are non-refundable." },
              { q: "What's included in the search report?", a: "Your report includes search results from all databases, analysis of potential conflicts, risk assessment, recommendations for proceeding, and guidance on strengthening your application." }
            ].map((faq, index) => (
              <Accordion key={index} type="single" collapsible>
                <AccordionItem value={`item-${index}`} className="bg-gradient-to-br from-slate-800/50 to-slate-900/50 backdrop-blur-sm border border-white/20 rounded-2xl px-6 py-2 hover:border-white/40 hover:shadow-xl hover:shadow-white/10 transition-all group">
                  <AccordionTrigger className="text-white font-bold text-left hover:text-amber-400 py-6 group-hover:scale-[1.01] transition-all">
                    <div className="flex items-start gap-4">
                      <div className="bg-gradient-to-br from-amber-500/20 to-amber-600/20 w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform">
                        <span className="text-amber-400 font-black text-lg">{index + 1}</span>
                      </div>
                      <span className="flex-1">{faq.q}</span>
                    </div>
                  </AccordionTrigger>
                  <AccordionContent className="text-gray-400 pl-14 pr-4 pb-6 leading-relaxed">
                    {faq.a}
                  </AccordionContent>
                </AccordionItem>
              </Accordion>
            ))}
          </div>

          {/* CTA below FAQ */}
          <div className="mt-12 text-center">
            <p className="text-gray-300 text-lg mb-6">Still have questions? We're here to help!</p>
            <Button 
              onClick={() => setIsPopupOpen(true)}
              className="bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 text-white font-bold shadow-xl hover:shadow-amber-500/30 hover:scale-105 transition-all px-8"
            >
              Contact Our Experts
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-slate-950">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="bg-gradient-to-br from-slate-900 to-slate-800 border border-white/20 rounded-2xl p-12 shadow-2xl">
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
              Protect Your Investment
            </h2>
            <p className="text-xl text-gray-300 mb-8">
              Don't risk rejection or legal issues. Get a comprehensive trademark search before filing.
            </p>
            <Button 
              size="lg" 
              onClick={() => setIsPopupOpen(true)}
              className="bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 text-white font-semibold shadow-xl transform transition-transform hover:scale-105"
            >
              Request Your Search
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </div>
        </div>
      </section>

      <ContactPopup isOpen={isPopupOpen} onClose={() => setIsPopupOpen(false)} />
    </div>
  );
};

export default TrademarkSearch;
